import React, { useEffect, useState } from "react";
import axios from "axios";
import API_URL from "../config";
import { motion } from "framer-motion";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import "@fullcalendar/common/main.css";
import "@fullcalendar/daygrid/main.css";
import { useIdioma } from "../context/IdiomaContext";
import { useEstilo } from "../context/EstiloContext";
import { obtenerEstilo } from "../temas/catalogoEstilos";
import { colorTextoLegible } from "../utils/contraste";

const formatHora12 = (hora) => {
  const [h, m] = hora.split(":");
  let hh = parseInt(h, 10);
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = hh % 12 || 12;
  return `${hh}:${m} ${ampm}`;
};

const esManana = (hora) => parseInt(hora.split(":")[0], 10) < 12;

const formatearFechaLegible = (fechaStr, idioma) => {
  const [anio, mes, dia] = fechaStr.split("-").map(Number);
  const fecha = new Date(anio, mes - 1, dia);
  const localeIntl = idioma === "de" ? "de-DE" : idioma === "en" ? "en-US" : "es-ES";
  return fecha.toLocaleDateString(localeIntl, { weekday: "long", day: "numeric", month: "long" });
};


const hoy = new Date().toLocaleDateString("en-CA");



const HorariosProfesor = ({ profesor, onVolver, onSeleccionarHorario, servicioActual, fondoEfectivo }) => {
  const { t, idioma } = useIdioma();
  const estiloGlobal = useEstilo();
  const estilo = servicioActual?.estilo_visual ? obtenerEstilo(servicioActual.estilo_visual) : estiloGlobal;
  const colorTexto = colorTextoLegible(fondoEfectivo);
  const [horarios, setHorarios] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [diasDisponibles, setDiasDisponibles] = useState([]);
  const [horarioElegido, setHorarioElegido] = useState(null);
  const panelRef = React.useRef(null);
  const calendarRef = React.useRef(null);
  const fullCalendarRef = React.useRef(null);

  /* 🔥 Al entrar a esta pantalla, centrar el calendario */
  useEffect(() => {
    calendarRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  useEffect(() => {
    if (selectedDate && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedDate, horarios]);

  const theme = {
    colorPrincipal: servicioActual?.color || "#2563eb",
    fondo: servicioActual?.color_fondo || "#f0f4ff",
  };

  /* 🔥 PROTECCIÓN: esperar que profesor exista */
  useEffect(() => {
    if (!profesor?.id) return;

   axios.get(`${API_URL}/api/horarios/disponibles/${profesor.id}`, {
     params: { servicio_id: servicioActual?.id },
   })

      .then((res) => {
const fechas = res.data.map((d) =>
  new Date(d.fecha).toLocaleDateString("en-CA")
);


        setDiasDisponibles(fechas);



      })
      .catch(err => console.error("Error cargando días:", err));

  }, [profesor, servicioActual]);

  // 🔥 apenas se sabe qué días hay disponibles, selecciona automáticamente
  // el más próximo — así el padre ve los horarios sin tener que hacer clic
  useEffect(() => {
    if (diasDisponibles.length === 0) return;

    const proximaFecha = diasDisponibles
      .filter((f) => f >= hoy)
      .sort()[0];

    if (proximaFecha) {
      seleccionarFecha(proximaFecha);
    }
  }, [diasDisponibles]);


const seleccionarFecha = async (fecha) => {
  if (!profesor?.id) return;

  if (!diasDisponibles.includes(fecha) || fecha < hoy) return;

  setSelectedDate(fecha);
  setHorarioElegido(null);
  fullCalendarRef.current?.getApi()?.gotoDate(fecha);

  try {

    const res = await axios.get(
      `${API_URL}/api/horarios/${profesor.id}/${fecha}`,
      { params: { servicio_id: servicioActual?.id } }
    );

    setHorarios(res.data || []);

  } catch (err) {
    console.error("Error cargando horarios:", err);
  }
};

// Wrapper para los eventos del calendario (select/dateClick), que mandan
// un objeto "info" en vez de la fecha directa
const handleDateSelect = (info) => {
  const fecha = info.dateStr || info.startStr.split("T")[0];
  seleccionarFecha(fecha);
};

// En el estilo "Portal Lateral" el clic no reserva de una vez — solo marca
// el horario elegido, y recién con "Continuar" se avanza a la reserva
const manejarClickHorario = (h) => {
  if (estilo.horariosConfirmar) {
    setHorarioElegido(h);
  } else {
    onSeleccionarHorario(h);
  }
};


  /* 🔥 PROTECCIÓN GLOBAL DEL RENDER */
  if (!profesor) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        Cargando profesor...
      </div>
    );
  }

  const horariosDisponibles = horarios.filter((h) => h.disponible === 1);
  const matutino = horariosDisponibles.filter((h) => esManana(h.hora_inicio));
  const vespertino = horariosDisponibles.filter((h) => !esManana(h.hora_inicio));

  return (
    <motion.div
      className="horarios-profesor-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        minHeight: "100vh",
        background: "transparent",
        padding: "20px 16px",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        style={styles.profesorCard}
      >
        <div style={styles.avatarWrapper}>
          <img
            src={profesor.foto}
onError={(e) =>
  (e.currentTarget.src =
    `${API_URL}/Fotos_profesores/default.png`)
}
          

            alt={profesor.nombre}
            style={{
              ...styles.avatar,
              border: `6px solid ${theme.colorPrincipal}`,
            }}
          />
        </div>

        <h2>{profesor.nombre}</h2>
        <p>{profesor.email}</p>
      </motion.div>

      <h3 style={{ ...styles.calendarTitle, color: colorTexto }}>
        {t("horarios_titulo")}
      </h3>

      {(() => {
        const calendarioJSX = (
          <div className="citas-calendar" style={estilo.horariosGridLateral ? styles.calendarBoxLateral : styles.calendarBox}>
            <FullCalendar
              ref={fullCalendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView={estilo.calendarView}
              locale={idioma}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridWeek,dayGridMonth",
              }}
              selectable
              select={handleDateSelect}
              dateClick={handleDateSelect}
              height="auto"
              showNonCurrentDates={false}
              fixedWeekCount={false}
              dayCellClassNames={(info) => {

               const fecha = info.date.toLocaleDateString("en-CA");

                const activa = diasDisponibles.includes(fecha) && fecha >= hoy;
                const clases = activa ? ["dia-disponible"] : ["dia-bloqueado"];
                if (fecha === selectedDate) clases.push("dia-seleccionado");
                return clases;
              }}
            />
          </div>
        );

        const horariosPanelJSX = (
          <>
            {selectedDate && (
              <p style={styles.fechaSeleccionadaTexto}>
                {t("horarios_fecha_seleccionada").replace("{fecha}", formatearFechaLegible(selectedDate, idioma))}
              </p>
            )}

            {matutino.length > 0 && (
              <>
                <h4 style={styles.sectionTitle}>{t("horarios_matutino")}</h4>
                <div style={estilo.horariosLista ? styles.horariosLista : styles.horariosRow}>
                  {matutino.map((h) => (
                    <HorarioCard
                      key={h.id}
                      h={h}
                      theme={theme}
                      estilo={estilo}
                      seleccionado={estilo.horariosConfirmar && horarioElegido?.id === h.id}
                      onSeleccionarHorario={manejarClickHorario}
                    />
                  ))}
                </div>
              </>
            )}

            {vespertino.length > 0 && (
              <>
                <h4 style={styles.sectionTitle}>{t("horarios_vespertino")}</h4>
                <div style={estilo.horariosLista ? styles.horariosLista : styles.horariosRow}>
                  {vespertino.map((h) => (
                    <HorarioCard
                      key={h.id}
                      h={h}
                      theme={theme}
                      estilo={estilo}
                      seleccionado={estilo.horariosConfirmar && horarioElegido?.id === h.id}
                      onSeleccionarHorario={manejarClickHorario}
                    />
                  ))}
                </div>
              </>
            )}

            {estilo.horariosConfirmar && (
              <button
                disabled={!horarioElegido}
                onClick={() => horarioElegido && onSeleccionarHorario(horarioElegido)}
                style={{
                  ...styles.btnContinuar,
                  background: theme.colorPrincipal,
                  borderRadius: estilo.botonRadius,
                  opacity: horarioElegido ? 1 : 0.5,
                  cursor: horarioElegido ? "pointer" : "not-allowed",
                }}
              >
                {t("portal_continuar")}
              </button>
            )}
          </>
        );

        if (estilo.horariosGridLateral) {
          return (
            <div style={styles.ladoALadoWrapper} ref={calendarRef}>
              {calendarioJSX}
              {selectedDate && (
                <motion.div
                  ref={panelRef}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={styles.panelLateral}
                >
                  {horariosPanelJSX}
                </motion.div>
              )}
            </div>
          );
        }

        return (
          <>
            <div style={styles.calendarWrapper} ref={calendarRef}>
              {calendarioJSX}
            </div>

            {selectedDate && (
              <motion.div
                ref={panelRef}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                style={styles.panel}
              >
                {horariosPanelJSX}
              </motion.div>
            )}
          </>
        );
      })()}

      <button
        onClick={onVolver}
        style={
          estilo.botonEstilo === "outline"
            ? { ...styles.btnVolver, background: "white", color: "#475569", border: "2px solid #475569", borderRadius: estilo.botonRadius }
            : { ...styles.btnVolver, borderRadius: estilo.botonRadius }
        }
      >
        {t("horarios_volver")}
      </button>

      <style>
        {`
          @media (max-width: 480px) {
            .horarios-profesor-page {
              padding: 12px 8px !important;
            }
          }

          .citas-calendar .fc {
            font-family: 'Poppins', sans-serif;
          }

          /* ENCABEZADO: que no se amontone en pantallas angostas */
          .citas-calendar .fc-header-toolbar {
            flex-wrap: wrap;
            gap: 10px;
            row-gap: 8px;
          }

          .citas-calendar .fc-toolbar-chunk {
            display: flex;
            align-items: center;
          }

          /* TÍTULO DEL MES */
          .citas-calendar .fc-toolbar-title {
            font-size: 1.1em;
            font-weight: 700;
            color: ${theme.colorPrincipal};
          }

          /* BOTONES (prev, next, today, vistas) */
          .citas-calendar .fc-button {
            background: ${theme.colorPrincipal} !important;
            border: none !important;
            border-radius: 10px !important;
            box-shadow: none !important;
            text-transform: capitalize;
            padding: 6px 12px !important;
            font-size: 0.85em !important;
            transition: transform .15s ease, opacity .15s ease;
          }

          @media (max-width: 480px) {
            .citas-calendar .fc-header-toolbar {
              justify-content: center;
              text-align: center;
            }
            .citas-calendar .fc-toolbar-title {
              font-size: 1em;
              width: 100%;
              order: -1;
            }
            .citas-calendar .fc-button {
              padding: 5px 9px !important;
              font-size: 0.78em !important;
            }
          }

          .citas-calendar .fc-button:hover {
            opacity: .85;
            transform: translateY(-1px);
          }

          .citas-calendar .fc-button-active {
            filter: brightness(0.85);
          }

          /* ENCABEZADO DE DÍAS DE LA SEMANA */
          .citas-calendar .fc-col-header-cell {
            background: #f8fafc;
            color: #475569;
            font-weight: 600;
            text-transform: uppercase;
            font-size: 0.8em;
            padding: 8px 0;
          }

          /* BORDES SUAVES DE LA GRILLA */
          .citas-calendar .fc-scrollgrid,
          .citas-calendar .fc-theme-standard td,
          .citas-calendar .fc-theme-standard th {
            border-color: #e2e8f0;
          }

          /* CELDAS DE DÍA — más compactas, sobre todo en Mes */
          .citas-calendar .fc-daygrid-day-frame {
            border-radius: 12px;
            margin: 3px;
            min-height: 44px !important;
            transition: transform .15s ease;
          }

          .citas-calendar .fc-daygrid-day-events {
            min-height: 0 !important;
          }

          @media (max-width: 480px) {
            .citas-calendar .fc-daygrid-day-frame {
              min-height: 34px !important;
              margin: 2px;
            }
            .citas-calendar .fc-daygrid-day-number {
              padding: 4px !important;
              font-size: 0.85em;
            }
          }

          .citas-calendar .fc-daygrid-day-number {
            padding: 8px;
            font-weight: 600;
          }

          .dia-bloqueado .fc-daygrid-day-frame {
            background: #f1f5f9 !important;
            color: #94a3b8 !important;
          }
          .dia-bloqueado {
            pointer-events: none;
          }

          .dia-disponible .fc-daygrid-day-frame {
            position: relative;
            background: ${theme.colorPrincipal} !important;
            color: white !important;
            font-weight: 700;
            box-shadow: 0 4px 10px ${theme.colorPrincipal}66;
          }
          .dia-disponible .fc-daygrid-day-frame::after {
            content: "✓";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 22px;
            font-weight: 900;
            color: white;
            pointer-events: none;
          }
          .dia-disponible .fc-daygrid-day-number {
            visibility: hidden;
          }
          .dia-disponible {
            cursor: pointer;
          }

          .dia-disponible:hover .fc-daygrid-day-frame {
            filter: brightness(1.1);
            transform: scale(1.05);
          }

          .dia-disponible .fc-daygrid-day-number {
            color: white !important;
          }

          /* DÍA SELECCIONADO — más oscuro/saturado y con un anillo, para
             que se note claramente cuál está elegido entre varios días
             disponibles */
          .dia-seleccionado .fc-daygrid-day-frame {
            filter: brightness(0.72) saturate(1.3);
            box-shadow: 0 0 0 3px white, 0 0 0 5px ${theme.colorPrincipal}, 0 6px 16px rgba(0,0,0,0.35) !important;
            transform: scale(1.06);
            z-index: 2;
          }
          .dia-seleccionado:hover .fc-daygrid-day-frame {
            filter: brightness(0.72) saturate(1.3);
            transform: scale(1.06);
          }

          /* DÍA DE HOY */
          .citas-calendar .fc-day-today .fc-daygrid-day-frame {
            box-shadow: inset 0 0 0 2px #f59e0b55;
          }
        `}
      </style>
    </motion.div>
  );
};

const HorarioCard = ({ h, onSeleccionarHorario, theme, estilo, seleccionado }) => {
  if (estilo?.horariosLista) {
    // ===== ESTILO "MODERNO" — fila completa, como una lista de franjas horarias =====
    return (
      <motion.div
        whileHover={{ backgroundColor: "#f8fafc" }}
        onClick={() => onSeleccionarHorario(h)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 18px",
          background: seleccionado ? `${theme.colorPrincipal}14` : "white",
          border: seleccionado ? `2px solid ${theme.colorPrincipal}` : `1px solid ${theme.colorPrincipal}33`,
          borderRadius: estilo.botonRadius,
          fontWeight: 600,
          color: "#1e293b",
          cursor: "pointer",
        }}
      >
        <span>{formatHora12(h.hora_inicio)} – {formatHora12(h.hora_fin)}</span>
        <span style={{ color: theme.colorPrincipal, fontSize: 18 }}>{seleccionado ? "✓" : "→"}</span>
      </motion.div>
    );
  }

  return (
    <motion.div
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => onSeleccionarHorario(h)}
      style={
        estilo?.botonEstilo === "outline"
          ? {
              padding: "14px 22px",
              background: seleccionado ? theme.colorPrincipal : "white",
              color: seleccionado ? "#fff" : theme.colorPrincipal,
              border: `2px solid ${theme.colorPrincipal}`,
              borderRadius: estilo.botonRadius,
              fontWeight: 600,
              cursor: "pointer",
            }
          : {
              padding: "14px 22px",
              background: theme.colorPrincipal,
              color: "#fff",
              borderRadius: estilo?.botonRadius ?? 14,
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: seleccionado ? `0 0 0 3px white, 0 0 0 5px ${theme.colorPrincipal}` : "none",
            }
      }
    >
      {formatHora12(h.hora_inicio)} – {formatHora12(h.hora_fin)}
    </motion.div>
  );
};

const styles = {
  profesorCard: {
    background: "#ffffff",
    padding: 18,
    borderRadius: 24,
    textAlign: "center",
    boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
    marginBottom: 20,
    maxWidth: 480,
    marginInline: "auto",
  },

  avatarWrapper: { display: "flex", justifyContent: "center", marginBottom: 10 },
  avatar: { width: 140, height: 140, borderRadius: "50%", objectFit: "cover" },

  calendarTitle: { textAlign: "center", marginBottom: 12, fontWeight: 600, fontSize: "26px" },
  calendarWrapper: { display: "flex", justifyContent: "center", marginBottom: 18 },
  calendarBox: { background: "#fff", padding: 14, borderRadius: 20, maxWidth: 900, width: "100%", boxShadow: "0 12px 30px rgba(0,0,0,0.15)" },

  panel: { background: "#fff", borderRadius: 20, padding: 18, maxWidth: 900, margin: "0 auto", boxShadow: "0 12px 30px rgba(0,0,0,0.15)" },
  sectionTitle: { textAlign: "center", marginBottom: 10 },
  fechaSeleccionadaTexto: { textAlign: "center", marginBottom: 14, fontSize: 14, fontWeight: 700, color: "#334155", textTransform: "capitalize" },
  horariosRow: { display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 14 },
  horariosLista: { display: "flex", flexDirection: "column", gap: 10, maxWidth: 420, marginInline: "auto" },
  btnVolver: { marginTop: 18, padding: "10px 24px", background: "#475569", color: "#fff", borderRadius: 16, border: "none", cursor: "pointer", display: "block", marginInline: "auto" },

  // ===== ESTILO "PORTAL LATERAL" — calendario y horarios apilados
  // (foto arriba, calendario, luego horarios — igual en celular y PC) =====
  ladoALadoWrapper: { display: "flex", flexDirection: "column", gap: 20, alignItems: "center", maxWidth: 720, marginInline: "auto", marginBottom: 18 },
  calendarBoxLateral: { background: "#fff", padding: 14, borderRadius: 20, width: "100%", boxShadow: "0 12px 30px rgba(0,0,0,0.15)" },
  panelLateral: { background: "#fff", borderRadius: 20, padding: 18, width: "100%", boxShadow: "0 12px 30px rgba(0,0,0,0.15)" },
  btnContinuar: { marginTop: 16, width: "100%", padding: "12px 24px", color: "#fff", border: "none", fontWeight: 700, fontSize: 14 },
};

export default HorariosProfesor;